// Modal for uploading images when importing a .tex file.
// Supports folder selection and drag-and-drop of entire project folders — auto-matches
// referenced image paths (including subdirectories) without requiring manual upload.

import { useState, useRef, useCallback } from "react";

interface ImageUploadModalProps {
    /** Image paths referenced in \includegraphics{...} */
    referencedFiles: string[];
    onConfirm: (images: Record<string, string>) => void;
    onSkip: () => void;
}

/** Image/figure formats accepted by pdflatex */
const FIGURE_EXTENSIONS = new Set([".png", ".jpg", ".jpeg", ".gif", ".svg", ".pdf", ".eps", ".bmp", ".tiff", ".tif", ".webp"]);

function hasFigureExtension(name: string): boolean {
    const ext = name.toLowerCase().match(/\.[^./\\]+$/)?.[0];
    return ext ? FIGURE_EXTENSIONS.has(ext) : false;
}

function readFileAsDataURL(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
}

/** Recursively read all files from a dropped FileSystemDirectoryEntry. */
async function readDirectoryEntry(
    entry: FileSystemDirectoryEntry,
    prefix = ""
): Promise<{ path: string; file: File }[]> {
    const results: { path: string; file: File }[] = [];
    const reader = entry.createReader();
    const readBatch = () =>
        new Promise<FileSystemEntry[]>((resolve, reject) => reader.readEntries(resolve, reject));
    let batch: FileSystemEntry[];
    do {
        batch = await readBatch();
        for (const child of batch) {
            const childPath = prefix ? `${prefix}/${child.name}` : child.name;
            if (child.isFile) {
                const file = await new Promise<File>((resolve, reject) =>
                    (child as FileSystemFileEntry).file(resolve, reject)
                );
                results.push({ path: childPath, file });
            } else if (child.isDirectory) {
                const sub = await readDirectoryEntry(child as FileSystemDirectoryEntry, childPath);
                results.push(...sub);
            }
        }
    } while (batch.length > 0);
    return results;
}

interface FolderEntry { path: string; file: File }

/** Build fast lookup maps over a set of folder entries. */
function buildLookupMaps(entries: FolderEntry[]) {
    const byPath = new Map<string, FolderEntry>();
    const byPathNoExt = new Map<string, FolderEntry>();
    const byBasename = new Map<string, FolderEntry>();
    const byBasenameNoExt = new Map<string, FolderEntry>();
    for (const entry of entries) {
        byPath.set(entry.path, entry);
        byPathNoExt.set(entry.path.replace(/\.[^./]+$/, ""), entry);
        byBasename.set(entry.file.name, entry);
        byBasenameNoExt.set(entry.file.name.replace(/\.[^.]+$/, ""), entry);
    }
    return { byPath, byPathNoExt, byBasename, byBasenameNoExt };
}

/** Find the best-matching folder entry for a \includegraphics reference. */
function findEntry(
    ref: string,
    maps: ReturnType<typeof buildLookupMaps>
): FolderEntry | undefined {
    const { byPath, byPathNoExt, byBasename, byBasenameNoExt } = maps;
    const basename = ref.split("/").pop() ?? ref;
    const basenameNoExt = basename.replace(/\.[^.]+$/, "");
    return (
        byPath.get(ref) ??
        byPathNoExt.get(ref) ??
        byBasename.get(basename) ??
        byBasenameNoExt.get(basenameNoExt)
    );
}

export function ImageUploadModal({ referencedFiles, onConfirm, onSkip }: ImageUploadModalProps) {
    const [images, setImages] = useState<Record<string, string>>({});
    const [isDragOver, setIsDragOver] = useState(false);
    const [autoStatus, setAutoStatus] = useState<{ matched: number; total: number } | null>(null);
    const [isScanning, setIsScanning] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const folderInputRef = useRef<HTMLInputElement>(null);

    /**
     * Core auto-match: given a flat list of { path, file } entries from a folder,
     * match each \includegraphics reference to its file and load it as a data URL.
     * Keys in the resulting map use the actual file path (with extension) so the
     * server writes the file at the correct relative location for pdflatex.
     */
    const autoMatchFromEntries = useCallback(
        async (allEntries: FolderEntry[]) => {
            setIsScanning(true);
            const figureEntries = allEntries.filter(
                (e) => hasFigureExtension(e.path) && e.file.size <= 20 * 1024 * 1024
            );
            const maps = buildLookupMaps(figureEntries);

            // If no specific refs, take all figure files found in the folder.
            const refs =
                referencedFiles.length > 0
                    ? referencedFiles
                    : figureEntries.map((e) => e.path);

            const newImages: Record<string, string> = {};
            let matchedCount = 0;

            for (const ref of refs) {
                const entry = findEntry(ref, maps);
                if (entry) {
                    // Use the actual file path (with extension) as the key so the server
                    // writes it in the correct subdirectory for pdflatex to find.
                    newImages[entry.path] = await readFileAsDataURL(entry.file);
                    matchedCount++;
                }
            }

            setAutoStatus({ matched: matchedCount, total: refs.length });
            setImages((prev) => ({ ...prev, ...newImages }));
            setIsScanning(false);

            // If everything resolved, confirm automatically — no action needed from user.
            if (matchedCount > 0 && matchedCount === refs.length) {
                onConfirm({ ...images, ...newImages });
            }
        },
        [referencedFiles, images, onConfirm]
    );

    /** Handle webkitdirectory folder input (file picker). */
    const handleFolderInput = useCallback(
        async (files: FileList) => {
            const entries: FolderEntry[] = [];
            for (const file of Array.from(files)) {
                const rel =
                    ((file as unknown as { webkitRelativePath?: string }).webkitRelativePath) ||
                    file.name;
                // Strip the root folder name (first component of the relative path).
                const parts = rel.split("/");
                const strippedPath = parts.length > 1 ? parts.slice(1).join("/") : rel;
                if (strippedPath) entries.push({ path: strippedPath, file });
            }
            await autoMatchFromEntries(entries);
        },
        [autoMatchFromEntries]
    );

    /** Handle individual file uploads (manual fallback). */
    const handleFiles = useCallback(
        async (files: FileList) => {
            const newImages: Record<string, string> = { ...images };
            for (const file of Array.from(files)) {
                if (!hasFigureExtension(file.name) && !file.type.startsWith("image/")) continue;
                if (file.size > 20 * 1024 * 1024) continue;
                const dataUrl = await readFileAsDataURL(file);
                newImages[file.name] = dataUrl;
            }
            setImages(newImages);
        },
        [images]
    );

    /** Handle drop — supports both individual files and entire folders. */
    const handleDrop = useCallback(
        async (e: React.DragEvent) => {
            e.preventDefault();
            setIsDragOver(false);

            const items = Array.from(e.dataTransfer.items);
            const entries: FolderEntry[] = [];
            let hasDirectory = false;

            for (const item of items) {
                const entry = item.webkitGetAsEntry?.();
                if (!entry) continue;
                if (entry.isDirectory) {
                    hasDirectory = true;
                    const sub = await readDirectoryEntry(entry as FileSystemDirectoryEntry);
                    entries.push(...sub);
                } else if (entry.isFile) {
                    const file = await new Promise<File>((resolve, reject) =>
                        (entry as FileSystemFileEntry).file(resolve, reject)
                    );
                    entries.push({ path: file.name, file });
                }
            }

            if (hasDirectory || entries.length > 0) {
                await autoMatchFromEntries(entries);
            } else if (e.dataTransfer.files.length) {
                await handleFiles(e.dataTransfer.files);
            }
        },
        [autoMatchFromEntries, handleFiles]
    );

    const handleRemove = useCallback((filename: string) => {
        setImages((prev) => {
            const next = { ...prev };
            delete next[filename];
            return next;
        });
    }, []);

    const uploadedCount = Object.keys(images).length;

    return (
        <div className="image-upload-modal-overlay" onClick={onSkip}>
            <div className="image-upload-modal" onClick={(e) => e.stopPropagation()}>
                <h3>Upload Project Images</h3>
                {referencedFiles.length > 0 ? (
                    <p>
                        This document references {referencedFiles.length} image
                        {referencedFiles.length !== 1 && "s"}:{" "}
                        {referencedFiles.map((f, i) => (
                            <span key={f}>
                                <code style={{ fontSize: 12 }}>{f}</code>
                                {i < referencedFiles.length - 1 && ", "}
                            </span>
                        ))}
                    </p>
                ) : (
                    <p>Upload images associated with this document.</p>
                )}

                {isScanning && (
                    <div className="image-auto-match-status image-auto-match-status--scanning">
                        Scanning folder for matching images…
                    </div>
                )}
                {!isScanning && autoStatus && (
                    <div
                        className={`image-auto-match-status ${autoStatus.matched === autoStatus.total
                                ? "image-auto-match-status--success"
                                : "image-auto-match-status--partial"
                            }`}
                    >
                        {autoStatus.matched === autoStatus.total
                            ? `✓ All ${autoStatus.matched} images found — importing…`
                            : `Found ${autoStatus.matched} of ${autoStatus.total} images automatically. Upload the rest below.`}
                    </div>
                )}

                {/* Hidden inputs */}
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,.pdf,.eps"
                    multiple
                    style={{ display: "none" }}
                    onChange={(e) => e.target.files && handleFiles(e.target.files)}
                />
                <input
                    ref={folderInputRef}
                    type="file"
                    // @ts-ignore — webkitdirectory is non-standard but supported in all major browsers
                    webkitdirectory=""
                    style={{ display: "none" }}
                    onChange={(e) => e.target.files && handleFolderInput(e.target.files)}
                />

                <div
                    className={`image-upload-dropzone ${isDragOver ? "image-upload-dropzone--active" : ""}`}
                    onDrop={handleDrop}
                    onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
                    onDragLeave={() => setIsDragOver(false)}
                    onClick={() => fileInputRef.current?.click()}
                >
                    {isDragOver
                        ? "Drop files or project folder here"
                        : "Drag & drop images or an entire project folder"}
                </div>

                <div className="image-upload-folder-row">
                    <button
                        className="image-upload-folder-btn"
                        onClick={(e) => { e.stopPropagation(); folderInputRef.current?.click(); }}
                        title="Select the folder containing your LaTeX project — images will be found and matched automatically"
                    >
                        Select Project Folder
                    </button>
                    <span className="image-upload-folder-hint">
                        Automatically finds all images in subdirectories
                    </span>
                </div>

                {uploadedCount > 0 && (
                    <div className="image-upload-list">
                        {Object.entries(images).map(([name, dataUrl]) => (
                            <div key={name} className="image-upload-item">
                                {dataUrl.startsWith("data:image/") ? (
                                    <img src={dataUrl} alt={name} />
                                ) : (
                                    <div className="image-upload-item-icon">📄</div>
                                )}
                                <span className="image-upload-item-name" title={name}>
                                    {name.split("/").pop()}
                                </span>
                                <button
                                    className="image-upload-item-remove"
                                    onClick={() => handleRemove(name)}
                                    title="Remove"
                                >
                                    ×
                                </button>
                            </div>
                        ))}
                    </div>
                )}

                <div className="image-upload-actions">
                    <button onClick={onSkip}>Skip</button>
                    <button onClick={() => onConfirm(images)}>
                        {uploadedCount > 0
                            ? `Import with ${uploadedCount} image${uploadedCount !== 1 ? "s" : ""}`
                            : "Import without images"}
                    </button>
                </div>
            </div>
        </div>
    );
}
